import { PureComponent } from 'react';
import { PatientRecordType, Period, Status } from '../api/NHDMConsentList';
import moment from 'moment';
import MedicalRecordsRightPopUp from './MedicalRecordsRightPopUp';
import { Table, Tag } from 'antd';
import AppLoader from '../components/common/AppLoader';
import { ColumnsType } from 'antd/lib/table';

export const statusOptions = {
    REQUESTED: {
        text: 'Pending',
        bgColor: 'bg-darwin-accent-bright-yellow-200',
        textColor: 'text-darwin-accent-bright-yellow-900',
        viewAllowed: false,
    },
    DENIED: {
        text: 'Denied',
        bgColor: 'bg-darwin-status-warning-100',
        textColor: 'text-darwin-status-warning-500',
        viewAllowed: false,
    },
    GRANTED: {
        text: 'Success',
        bgColor: 'bg-darwin-accent-green-100',
        textColor: 'text-darwin-accent-green-900',
        viewAllowed: true,
    },
    APPROVAL_ERROR: {
        text: 'Error',
        bgColor: 'bg-darwin-status-warning-100',
        textColor: 'text-darwin-status-warning-500',
        viewAllowed: false,
    },
    ERROR: {
        text: 'Error',
        bgColor: 'bg-darwin-status-warning-100',
        textColor: 'text-darwin-status-warning-500',
        viewAllowed: false,
    },
    EXPIRED: {
        text: 'Expired',
        bgColor: 'bg-darwin-status-warning-100',
        textColor: 'text-darwin-status-warning-500',
        viewAllowed: false,
    },
    InitError: {
        text: 'Error',
        bgColor: 'bg-darwin-status-warning-100',
        textColor: 'text-darwin-status-warning-500',
        viewAllowed: false,
    },
    REVOKED: {
        text: 'Revoked',
        bgColor: 'bg-darwin-status-warning-100',
        textColor: 'text-darwin-status-warning-500',
        viewAllowed: false,
    },
};

function daysBetween(to: string, from: string) {
    const fromDate = moment(from).utc().format('YYYY-MM-DD');
    const toDate = moment(to).utc().format('YYYY-MM-DD');
    return Math.floor((Date.parse(toDate) - Date.parse(fromDate)) / 86400000);
}
function expiresIn(to: string) {
    return daysBetween(to, new Date().toString()) < 0
        ? 'Expired'
        : daysBetween(to, new Date().toString());
}
function expiresTextColor(days: number | 'Expired') {
    if (days === 'Expired') {
        return 'text-darwin-neutral-500';
    }
    if (days <= 5) {
        return 'text-darwin-status-warning-500';
    }
    if (days <= 10) {
        return 'text-darwin-accent-bright-yellow-800';
    }
    return 'text-darwin-accent-green-900';
}
type State = {
    activeConsentId: string | undefined;
};
type Props = {
    patientRecords: PatientRecordType[];
    isLoading: boolean | undefined;
    plId: string;
};

export class MedicalRecordsTableOld extends PureComponent<Props, State> {
    state: State = {
        activeConsentId: undefined,
    };
    columns: ColumnsType<PatientRecordType> = [
        {
            title: <span className="font-700 text-14 text-darwin-neutral-700">CONSENT ID</span>,
            dataIndex: 'consent_id',
            key: 'consent_id',
            width: 300,
            render: (consentId: string) => {
                return <p className="text-14 text-darwin-neutral-900">{consentId}</p>;
            },
        },
        {
            title: <span className="font-700 text-14 text-darwin-neutral-700">REQUESTED ON</span>,
            dataIndex: 'c_at',
            key: 'c_at',
            width: 140,
            render: (c_at: string) => {
                return (
                    <>
                        <p className="text-14 text-darwin-neutral-900">
                            {moment(c_at).utc().format("DD MMM'YY")}
                        </p>
                        <p className="text-12 text-darwin-neutral-500">
                            {moment(c_at).format('hh:mm A')}
                        </p>
                    </>
                );
            },
        },
        {
            title: <span className="font-700 text-14 text-darwin-neutral-700">LAST UPDATED</span>,
            dataIndex: 'u_at',
            key: 'u_at',
            render: (u_at: string) => {
                return (
                    <>
                        <p className="text-14 text-darwin-neutral-900">
                            {moment(u_at).utc().format("DD MMM'YY")}
                        </p>
                        <p className="text-12 text-darwin-neutral-500">
                            {moment(u_at).format('hh:mm A')}
                        </p>
                    </>
                );
            },
        },
        {
            title: <span className="font-700 text-14 text-darwin-neutral-700">SHARED FOR</span>,
            dataIndex: 'period',
            key: 'period',
            render: (period: Period) => {
                return (
                    <p className="text-14 text-darwin-neutral-900">
                        {daysBetween(period.to, period.from)} Days
                    </p>
                );
            },
        },
        {
            title: <span className="font-700 text-14 text-darwin-neutral-700">EXPIRES IN</span>,
            dataIndex: 'period',
            key: 'expiry',
            render: (period: Period) => {
                return (
                    <>
                        <p className={`font-700 ${expiresTextColor(expiresIn(period.expiry))}`}>
                            {expiresIn(period.expiry)}{' '}
                            {expiresIn(period.expiry) === 'Expired' ? '' : 'days'}
                        </p>
                        <span className="text-12 text-darwin-neutral-500">
                            {moment(period.expiry).utc().format("DD MMM'YY")}
                        </span>
                    </>
                );
            },
        },
        {
            title: <span className="font-700 text-14 text-darwin-neutral-700">STATUS</span>,
            key: 'status',
            dataIndex: 'status',
            render: (status: Status) => {
                if (!statusOptions[status]) {
                    return '-';
                }
                return (
                    <Tag className={`border-none rounded-16 ${statusOptions[status]?.bgColor}`}>
                        <span
                            style={{
                                color: statusOptions[status]?.textColor,
                            }}
                            className={`font-700 py-5 px-4 ${statusOptions[status]?.textColor}`}
                        >
                            {statusOptions[status]?.text}
                        </span>
                    </Tag>
                );
            },
        },
        {
            title: <span className="font-700 text-14 text-darwin-neutral-700">ACTION</span>,
            key: 'action',
            render: (patientRecord: PatientRecordType) => {
                return statusOptions[patientRecord.status]?.viewAllowed ? (
                    <p
                        className="font-700 text-darwin-primary-500 cursor-pointer"
                        onClick={() => {
                            this.setState({
                                activeConsentId: patientRecord.consent_id,
                            });
                        }}
                    >
                        View
                    </p>
                ) : (
                    <p className="font-700 text-darwin-primary-500 cursor-pointer">-</p>
                );
            },
        },
    ];
    clearConsentId = () => {
        this.setState({ activeConsentId: undefined });
    };
    render() {
        const { activeConsentId } = this.state;
        const { plId, isLoading, patientRecords } = this.props;

        if (isLoading) {
            return (
                <div className="flex items-center justify-center h-full w-full">
                    <AppLoader />
                </div>
            );
        }

        return (
            <>
                <div className="overflow-auto w-full">
                    <Table
                        className="border-darwin-neutral-50"
                        bordered={true}
                        pagination={false}
                        columns={this.columns}
                        dataSource={patientRecords}
                        sticky={true}
                        locale={{ emptyText: <p>No Medical Records Found.</p> }}
                    />
                </div>
                {activeConsentId && (
                    <MedicalRecordsRightPopUp
                        consentId={activeConsentId}
                        clearConsentId={this.clearConsentId}
                        plId={plId}
                    />
                )}
            </>
        );
    }
}
